import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Alert,
  Skeleton,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Payments as PaymentsIcon,
  TrendingUp as TrendingUpIcon,
  Campaign as CampaignIcon,
  Sell as SellIcon,
  CheckCircle as CheckCircleIcon,
  Storefront as StorefrontIcon,
  Insights as InsightsIcon,
  ArrowForward as ArrowForwardIcon,
  Inventory2 as Inventory2Icon,
  LocalShipping as LocalShippingIcon,
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  AccountBalance as AccountBalanceIcon,
  EventNote as EventNoteIcon,
  Science as ScienceIcon,
  SmartToy as SmartToyIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import { PageHeader, StatCard } from '../components/ui';
import { useIsMobile } from '../utils/responsive';
import { MP_COLORS } from '../utils/productImage';
import { getMenuItems } from '../utils/menuConfig';
import axios from '../utils/axios';

// ============================================================
// Константы и хелперы
// ============================================================

const MP_NAMES = { 1: 'Wildberries', 2: 'Ozon', 3: 'Yandex Market' };
const MP_SHORT = { 1: 'WB', 2: 'Ozon', 3: 'Yandex' };

const PRESET_META = {
  director: { label: 'Руководитель', icon: <InsightsIcon fontSize="small" /> },
  manager: { label: 'Менеджер МП', icon: <StorefrontIcon fontSize="small" /> },
  purchaser: { label: 'Закупщик', icon: <Inventory2Icon fontSize="small" /> },
};

const PRESET_STORAGE_KEY = 'dashboard_preset';

// --- Даты ---
const pad2 = (n) => String(n).padStart(2, '0');
const toYmd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const addDays = (base, n) => {
  const x = new Date(base);
  x.setDate(x.getDate() + n);
  return x;
};
// dt (YYYY-MM-DD) -> дд.мм для осей графика
const shortDate = (dt) => (dt && dt.length >= 10 ? `${dt.slice(8, 10)}.${dt.slice(5, 7)}` : dt);

// --- Форматтеры (ru-RU) ---
const fmtInt = (n) => Number(n || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtMoney = (n) => `${Math.round(Number(n || 0)).toLocaleString('ru-RU')} ₽`;
const fmtPct = (n) => `${Number(n || 0).toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

// Проценты изменения периода к периоду
function computeTrend(cur, prev, prevHasData) {
  if (!prevHasData) return null;
  let pct;
  if (prev === 0) pct = cur > 0 ? 100 : cur < 0 ? -100 : 0;
  else pct = ((cur - prev) / Math.abs(prev)) * 100;
  return {
    trend: pct >= 0 ? 'up' : 'down',
    trendValue: `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%`,
  };
}

// Агрегация строк /daily по набору дат
function sumDailyRows(rowsMap, dates) {
  let ordersCount = 0;
  let ordersSumRub = 0;
  let profit = 0;
  let advSum = 0;
  let buyoutWeighted = 0;
  let buyoutOrders = 0;
  let hasData = false;
  for (const dt of dates) {
    const r = rowsMap.get(dt);
    if (!r) continue;
    hasData = true;
    ordersCount += Number(r.ordersCount || 0);
    ordersSumRub += Number(r.ordersSumRub || 0);
    profit += Number(r.profit || 0);
    advSum += Number(r.advSum || 0);
    buyoutWeighted += Number(r.buyoutPercent || 0) * Number(r.ordersCount || 0);
    buyoutOrders += Number(r.ordersCount || 0);
  }
  return {
    ordersCount,
    ordersSumRub,
    profit,
    advSum,
    avgPrice: ordersCount ? ordersSumRub / ordersCount : 0,
    buyoutPercent: buyoutOrders ? buyoutWeighted / buyoutOrders : 0,
    drr: ordersSumRub ? (100 * advSum) / ordersSumRub : 0,
    hasData,
  };
}

// ============================================================
// Мелкие переиспользуемые блоки
// ============================================================

function SectionCard({ title, subtitle, action, children, sx }) {
  return (
    <Card sx={{ height: '100%', ...sx }}>
      <CardContent>
        {(title || action) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2,
              gap: 1,
            }}
          >
            <Box>
              {title && (
                <Typography variant="titleMedium" fontWeight={600}>
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="bodySmall" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            {action}
          </Box>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

function BlockState({ loading, error, empty, emptyText = 'Нет данных за период', minHeight = 120, children }) {
  if (loading) {
    return (
      <Box sx={{ minHeight }}>
        <Skeleton variant="rounded" height={minHeight} />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
        {error}
      </Alert>
    );
  }
  if (empty) {
    return (
      <Box
        sx={{
          minHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <Typography variant="bodyMedium">{emptyText}</Typography>
      </Box>
    );
  }
  return children;
}

// Карточка быстрого перехода
function QuickLink({ label, icon, color = 'primary', onClick }) {
  const theme = useTheme();
  const paletteColor = theme.palette[color] || theme.palette.primary;
  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea onClick={onClick} sx={{ height: '100%', p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(paletteColor.main, 0.14),
              color: paletteColor.main,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="titleSmall" noWrap>
              {label}
            </Typography>
          </Box>
          <ArrowForwardIcon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.6 }} />
        </Stack>
      </CardActionArea>
    </Card>
  );
}

// Простой горизонтальный топ-список (подгруппы/артикулы)
function TopList({ items, total, color = 'primary' }) {
  const theme = useTheme();
  const paletteColor = theme.palette[color] || theme.palette.primary;
  const max = items.reduce((m, it) => Math.max(m, it.value), 0) || 1;
  return (
    <Stack spacing={1.25}>
      {items.map((it) => {
        const share = total ? (100 * it.value) / total : 0;
        return (
          <Box key={it.label}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
              <Typography variant="bodySmall" noWrap sx={{ flex: 1 }} title={it.label}>
                {it.label}
              </Typography>
              <Typography variant="bodySmall" color="text.secondary" sx={{ flexShrink: 0 }}>
                {fmtInt(it.value)} шт · {fmtPct(share)}
              </Typography>
            </Box>
            <Box sx={{ height: 6, borderRadius: 3, bgcolor: alpha(paletteColor.main, 0.12) }}>
              <Box
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  width: `${Math.max(3, (100 * it.value) / max)}%`,
                  bgcolor: paletteColor.main,
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

// График динамики за 14 дней с переключателем метрики (Сумма ₽ / Заказы шт)
function DynamicsChart({ theme, title, data, loading, error, defaultMetric = 'sum' }) {
  const [metric, setMetric] = useState(defaultMetric);
  const isSum = metric === 'sum';
  const dataKey = isSum ? 'ordersSumRub' : 'ordersCount';
  const color = isSum ? theme.palette.primary.main : theme.palette.secondary.main;
  const hasData = data.some((d) => d.ordersSumRub || d.ordersCount);

  const toggle = (
    <ToggleButtonGroup value={metric} exclusive size="small" onChange={(_e, v) => v && setMetric(v)}>
      <ToggleButton value="sum" sx={{ textTransform: 'none' }}>
        Сумма
      </ToggleButton>
      <ToggleButton value="orders" sx={{ textTransform: 'none' }}>
        Заказы
      </ToggleButton>
    </ToggleButtonGroup>
  );

  return (
    <SectionCard title={title} action={toggle}>
      <BlockState loading={loading} error={error} empty={!loading && !error && !hasData} minHeight={300}>
        <Box sx={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="dashDynamics" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
              <XAxis dataKey="label" axisLine={false} tickLine={false} fontSize={12} />
              <YAxis
                axisLine={false}
                tickLine={false}
                fontSize={12}
                allowDecimals={false}
                tickFormatter={(v) => (isSum ? (v >= 1000 ? `${Math.round(v / 1000)}K` : v) : fmtInt(v))}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                }}
                formatter={(value) =>
                  isSum ? [fmtMoney(value), 'Сумма заказов'] : [`${fmtInt(value)} шт`, 'Заказы']
                }
              />
              <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fill="url(#dashDynamics)" />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </BlockState>
    </SectionCard>
  );
}

// ============================================================
// Главная страница
// ============================================================

export default function Dashboard() {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);

  const isAdmin = user?.role === 'admin';
  const hasPerm = useCallback(
    (key) => {
      if (!user) return false;
      if (isAdmin) return true;
      return user.permissions?.[key] === true;
    },
    [user, isAdmin]
  );

  // Открытые пользователю МП (1=WB, 2=Ozon, 3=Yandex)
  const allowedMps = useMemo(() => {
    if (isAdmin) return [1, 2, 3];
    const ds = user?.data_scope;
    if (!ds) return [1, 2, 3];
    const mps = ds.marketplaces;
    if (!Array.isArray(mps) || mps.length === 0) return [1, 2, 3];
    const filtered = mps.map(Number).filter((n) => [1, 2, 3].includes(n));
    return filtered.length ? filtered : [1, 2, 3];
  }, [user, isAdmin]);

  const canFinance = hasPerm('analyticsFinance');
  const canPurchasing = hasPerm('purchasingDashboard');
  const canSupply = hasPerm('supplyPlanning');
  const fullMpAccess = allowedMps.length === 3;

  // Доступные пресеты
  const availablePresets = useMemo(() => {
    const list = [];
    if (canFinance && fullMpAccess) list.push('director');
    if (canFinance) list.push('manager');
    if (canPurchasing || canSupply) list.push('purchaser');
    return list;
  }, [canFinance, fullMpAccess, canPurchasing, canSupply]);

  // Пресет по умолчанию (авто-подбор)
  const defaultPreset = useMemo(() => {
    if (canFinance && !fullMpAccess) return 'manager';
    if (canFinance && fullMpAccess) return 'director';
    if (canPurchasing || canSupply) return 'purchaser';
    return null;
  }, [canFinance, fullMpAccess, canPurchasing, canSupply]);

  // Активный пресет с безопасным откатом и сохранением выбора
  const [preset, setPreset] = useState(null);
  useEffect(() => {
    if (availablePresets.length === 0) {
      setPreset(null);
      return;
    }
    let saved = null;
    try {
      saved = localStorage.getItem(PRESET_STORAGE_KEY);
    } catch {
      saved = null;
    }
    if (saved && availablePresets.includes(saved)) setPreset(saved);
    else setPreset(defaultPreset && availablePresets.includes(defaultPreset) ? defaultPreset : availablePresets[0]);
  }, [availablePresets, defaultPreset]);

  const handlePresetChange = (_e, value) => {
    if (!value || !availablePresets.includes(value)) return;
    setPreset(value);
    try {
      localStorage.setItem(PRESET_STORAGE_KEY, value);
    } catch {
      /* localStorage недоступен — не критично */
    }
  };

  // Выбранный МП для пресета «Менеджер МП»
  const [managerMp, setManagerMp] = useState(allowedMps[0] || 1);
  useEffect(() => {
    if (!allowedMps.includes(managerMp)) setManagerMp(allowedMps[0] || 1);
  }, [allowedMps, managerMp]);

  // ----------------------------------------------------------
  // Диапазоны дат: последние 14 дней, split на текущие/прошлые 7
  // ----------------------------------------------------------
  const { days14, current7, prev7, dateFrom14, dateTo14, dateFrom7, dateTo7 } = useMemo(() => {
    const today = new Date();
    const list = Array.from({ length: 14 }, (_, i) => toYmd(addDays(today, -13 + i)));
    return {
      days14: list,
      current7: list.slice(7),
      prev7: list.slice(0, 7),
      dateFrom14: list[0],
      dateTo14: list[13],
      dateFrom7: list[7],
      dateTo7: list[13],
    };
  }, []);

  // ----------------------------------------------------------
  // Состояния данных блоков
  // ----------------------------------------------------------
  const [finance, setFinance] = useState({ loading: false, error: null, rows: null });
  const [mpBreakdown, setMpBreakdown] = useState({ loading: false, error: null, data: null });
  const [purchasing, setPurchasing] = useState({ loading: false, error: null, data: null });

  // --- Оперативные метрики (Руководитель / Менеджер) ---
  useEffect(() => {
    if (preset !== 'director' && preset !== 'manager') return;
    if (!canFinance) return;
    let cancelled = false;
    const mp = preset === 'director' ? 0 : managerMp;
    setFinance({ loading: true, error: null, rows: null });
    axios
      .get('/analytics/wb/statistic/daily', {
        params: { mp, date_from: dateFrom14, date_to: dateTo14 },
      })
      .then((res) => {
        if (cancelled) return;
        setFinance({ loading: false, error: null, rows: res.data?.rows || [] });
      })
      .catch((e) => {
        if (cancelled) return;
        setFinance({
          loading: false,
          error: e?.response?.data?.message || 'Не удалось загрузить оперативные метрики',
          rows: null,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [preset, managerMp, canFinance, dateFrom14, dateTo14]);

  // --- Разбивка по МП (только Руководитель) ---
  useEffect(() => {
    if (preset !== 'director' || !canFinance) return;
    let cancelled = false;
    setMpBreakdown({ loading: true, error: null, data: null });
    Promise.all(
      allowedMps.map((mp) =>
        axios
          .get('/analytics/wb/statistic/daily', {
            params: { mp, date_from: dateFrom7, date_to: dateTo7 },
            silent: true,
          })
          .then((res) => ({ mp, rows: res.data?.rows || [] }))
          .catch(() => ({ mp, rows: [], failed: true }))
      )
    ).then((results) => {
      if (cancelled) return;
      const data = results.map(({ mp, rows }) => {
        const map = new Map(rows.map((r) => [r.dt, r]));
        const agg = sumDailyRows(map, current7);
        return { mp, ...agg };
      });
      setMpBreakdown({ loading: false, error: null, data });
    });
    return () => {
      cancelled = true;
    };
  }, [preset, canFinance, allowedMps, dateFrom7, dateTo7]);

  // --- Закупочный блок ---
  useEffect(() => {
    if (preset !== 'purchaser' || !canPurchasing) return;
    let cancelled = false;
    setPurchasing({ loading: true, error: null, data: null });
    axios
      .get('/analytics/purchasing/dashboard', {
        params: { date_from: dateFrom14, date_to: dateTo14 },
      })
      .then((res) => {
        if (cancelled) return;
        setPurchasing({ loading: false, error: null, data: res.data || { byDate: [], byProduct: [], byColor: [] } });
      })
      .catch((e) => {
        if (cancelled) return;
        setPurchasing({
          loading: false,
          error: e?.response?.data?.message || 'Не удалось загрузить закупочные данные',
          data: null,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [preset, canPurchasing, dateFrom14, dateTo14]);

  // ----------------------------------------------------------
  // Производные данные
  // ----------------------------------------------------------
  const financeRowsMap = useMemo(() => {
    if (!finance.rows) return null;
    return new Map(finance.rows.map((r) => [r.dt, r]));
  }, [finance.rows]);

  const kpi = useMemo(() => {
    if (!financeRowsMap) return null;
    const cur = sumDailyRows(financeRowsMap, current7);
    const prev = sumDailyRows(financeRowsMap, prev7);
    return { cur, prev };
  }, [financeRowsMap, current7, prev7]);

  const chartData = useMemo(() => {
    if (!financeRowsMap) return [];
    return days14.map((dt) => {
      const r = financeRowsMap.get(dt);
      return {
        dt,
        label: shortDate(dt),
        ordersSumRub: Number(r?.ordersSumRub || 0),
        ordersCount: Number(r?.ordersCount || 0),
        profit: Number(r?.profit || 0),
      };
    });
  }, [financeRowsMap, days14]);

  const purchasingChart = useMemo(() => {
    if (!purchasing.data) return [];
      const byDateMap = new Map((purchasing.data.byDate || []).map((r) => [r.dt, Number(r.unitsCount ?? r.ordersCount ?? 0)]));
    return days14.map((dt) => ({ dt, label: shortDate(dt), unitsCount: byDateMap.get(dt) || 0 }));
  }, [purchasing.data, days14]);

  // ----------------------------------------------------------
  // Быстрые переходы (гейт по permissions)
  // ----------------------------------------------------------
  const quickLinksFor = useCallback(
    (presetKey) => {
      const defs = {
        director: [
          { perm: 'analyticsFinance', label: 'Финансы', description: 'Отчёты по МП', icon: <AccountBalanceIcon fontSize="small" />, color: 'success', path: '/analytics/finance' },
          { perm: 'analyticsPnl', label: 'P&L', description: 'Прибыли и убытки', icon: <AssessmentIcon fontSize="small" />, color: 'info', path: '/analytics/pnl' },
          { perm: 'salesPlans', label: 'Планы продаж', description: 'Прогноз и факт', icon: <EventNoteIcon fontSize="small" />, color: 'primary', path: '/analytics/plans' },
          { perm: 'analyticsFinance', label: 'Ежедневная аналитика', description: 'Динамика по дням', icon: <InsightsIcon fontSize="small" />, color: 'secondary', path: '/analytics/daily' },
          { perm: 'aiEffectiveness', label: 'AI Эффективность', description: 'Отдача рекомендаций', icon: <SmartToyIcon fontSize="small" />, color: 'warning', path: '/analytics/ai-effectiveness' },
          { perm: 'forecastLab', label: 'Forecast Lab', description: 'Модели прогноза', icon: <ScienceIcon fontSize="small" />, color: 'info', path: '/analytics/forecast-lab' },
        ],
        manager: [
          { perm: 'analyticsFinance', label: 'Ежедневная аналитика', description: 'Метрики по дням', icon: <InsightsIcon fontSize="small" />, color: 'primary', path: '/analytics/daily' },
          { perm: 'analyticsAdvert', label: 'Реклама', description: 'Кампании и ДРР', icon: <CampaignIcon fontSize="small" />, color: 'warning', path: '/analytics/advert' },
          { perm: 'analyticsSearch', label: 'Поиск WB', description: 'Позиции и запросы', icon: <SearchIcon fontSize="small" />, color: 'secondary', path: '/analytics/search' },
          { perm: 'analyticsSupplies', label: 'Поставки', description: 'Статусы поставок', icon: <LocalShippingIcon fontSize="small" />, color: 'info', path: '/analytics/supplies' },
          { perm: 'products', label: 'Товары', description: 'Каталог и карточки', icon: <CategoryIcon fontSize="small" />, color: 'success', path: '/analytics/products' },
          { perm: 'analyticsSales', label: 'По артикулам', description: 'ROI по SKU', icon: <SellIcon fontSize="small" />, color: 'primary', path: '/analytics/sales' },
        ],
        purchaser: [
          { perm: 'purchasingDashboard', label: 'Закупки', description: 'Дашборд закупок', icon: <ShoppingCartIcon fontSize="small" />, color: 'primary', path: '/analytics/purchasing' },
          { perm: 'supplyPlanning', label: 'Поставки FBO', description: 'Планирование поставок', icon: <LocalShippingIcon fontSize="small" />, color: 'info', path: '/analytics/supply-plan' },
          { perm: 'products', label: 'Товары', description: 'Каталог и карточки', icon: <CategoryIcon fontSize="small" />, color: 'success', path: '/analytics/products' },
          { perm: 'products', label: 'Остатки и себестоимость', description: 'Оценка склада', icon: <Inventory2Icon fontSize="small" />, color: 'secondary', path: '/analytics/stocks' },
        ],
      };
      return (defs[presetKey] || []).filter((l) => hasPerm(l.perm));
    },
    [hasPerm]
  );

  // ----------------------------------------------------------
  // Рендер
  // ----------------------------------------------------------
  return (
    <Box>
      <PageHeader
        title="Дашборд"
        actions={
          availablePresets.length > 1 ? (
            <ToggleButtonGroup
              value={preset}
              exclusive
              onChange={handlePresetChange}
              size="small"
              orientation={isMobile ? 'vertical' : 'horizontal'}
            >
              {availablePresets.map((p) => (
                <ToggleButton key={p} value={p} sx={{ textTransform: 'none', gap: 0.75 }}>
                  {PRESET_META[p].icon}
                  {PRESET_META[p].label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          ) : null
        }
      />

      {preset === 'director' && (
        <DirectorView
          theme={theme}
          kpi={kpi}
          finance={finance}
          chartData={chartData}
          mpBreakdown={mpBreakdown}
          allowedMps={allowedMps}
          quickLinks={quickLinksFor('director')}
          navigate={navigate}
        />
      )}

      {preset === 'manager' && (
        <ManagerView
          theme={theme}
          kpi={kpi}
          finance={finance}
          chartData={chartData}
          allowedMps={allowedMps}
          managerMp={managerMp}
          setManagerMp={setManagerMp}
          quickLinks={quickLinksFor('manager')}
          navigate={navigate}
        />
      )}

      {preset === 'purchaser' && (
        <PurchaserView
          theme={theme}
          canPurchasing={canPurchasing}
          purchasing={purchasing}
          purchasingChart={purchasingChart}
          quickLinks={quickLinksFor('purchaser')}
          navigate={navigate}
        />
      )}

      {!preset && <NavigationalView user={user} navigate={navigate} />}
    </Box>
  );
}

// ============================================================
// KPI-набор для оперативных пресетов
// ============================================================

function FinanceKpiGrid({ kpi, extended }) {
  if (!kpi) return null;
  const { cur, prev } = kpi;
  const prevHas = prev.hasData;

  const base = [
    {
      title: 'Заказы',
      value: fmtInt(cur.ordersCount),
      icon: <ShoppingCartIcon />,
      color: 'primary',
      ...(computeTrend(cur.ordersCount, prev.ordersCount, prevHas) || {}),
    },
    {
      title: 'Сумма заказов',
      value: fmtMoney(cur.ordersSumRub),
      icon: <PaymentsIcon />,
      color: 'success',
      ...(computeTrend(cur.ordersSumRub, prev.ordersSumRub, prevHas) || {}),
    },
    {
      title: 'Оперативный профит',
      value: fmtMoney(cur.profit),
      icon: <TrendingUpIcon />,
      color: cur.profit >= 0 ? 'success' : 'error',
      ...(computeTrend(cur.profit, prev.profit, prevHas) || {}),
    },
    {
      title: 'Реклама',
      value: fmtMoney(cur.advSum),
      icon: <CampaignIcon />,
      color: 'warning',
      ...(computeTrend(cur.advSum, prev.advSum, prevHas) || {}),
    },
    {
      title: 'ДРР',
      value: fmtPct(cur.drr),
      icon: <InsightsIcon />,
      color: 'warning',
    },
  ];

  const extra = extended
    ? [
        {
          title: 'Средняя цена',
          value: fmtMoney(cur.avgPrice),
          icon: <SellIcon />,
          color: 'info',
        },
        {
          title: 'Процент выкупа',
          value: fmtPct(cur.buyoutPercent),
          icon: <CheckCircleIcon />,
          color: 'secondary',
        },
      ]
    : [];

  const cards = [...base, ...extra];
  // director: 5 карточек в ряд (md=2.4), менеджер: 7 карточек по 4 в ряд (md=3)
  const mdCols = extended ? 3 : 2.4;

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map((c) => (
        <Grid item xs={12} sm={6} md={mdCols} key={c.title}>
          <StatCard {...c} />
        </Grid>
      ))}
    </Grid>
  );
}

// ============================================================
// Пресет «Руководитель»
// ============================================================

function DirectorView({ theme, kpi, finance, chartData, mpBreakdown, quickLinks, navigate }) {
  return (
    <>
      <BlockState
        loading={finance.loading}
        error={finance.error}
        empty={!finance.loading && !finance.error && kpi && !kpi.cur.hasData}
        minHeight={140}
      >
        {kpi && <FinanceKpiGrid kpi={kpi} extended={false} />}
      </BlockState>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <DynamicsChart
            theme={theme}
            title="Динамика за 14 дней"
            data={chartData}
            loading={finance.loading}
            error={finance.error}
          />
        </Grid>

        <Grid item xs={12} lg={4}>
          <SectionCard title="Разбивка по маркетплейсам">
            <BlockState
              loading={mpBreakdown.loading}
              error={mpBreakdown.error}
              empty={
                !mpBreakdown.loading &&
                !mpBreakdown.error &&
                (!mpBreakdown.data || mpBreakdown.data.every((d) => !d.hasData))
              }
              minHeight={200}
            >
              <Stack spacing={1.5} divider={<Divider flexItem />}>
                {(mpBreakdown.data || []).map((d) => (
                  <Box key={d.mp}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: MP_COLORS[d.mp] || theme.palette.primary.main,
                        }}
                      />
                      <Typography variant="titleSmall">{MP_NAMES[d.mp]}</Typography>
                    </Box>
                    <Typography variant="bodyMedium" fontWeight={600} sx={{ pl: 2 }}>
                      {fmtInt(d.ordersCount)} шт · {fmtMoney(d.ordersSumRub)} ·{' '}
                      <Box
                        component="span"
                        sx={{ color: d.profit >= 0 ? 'success.main' : 'error.main' }}
                      >
                        {fmtMoney(d.profit)}
                      </Box>
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </BlockState>
          </SectionCard>
        </Grid>
      </Grid>

      <QuickLinksSection title="Быстрые переходы" links={quickLinks} navigate={navigate} />
    </>
  );
}

// ============================================================
// Пресет «Менеджер МП»
// ============================================================

function ManagerView({ theme, kpi, finance, chartData, allowedMps, managerMp, setManagerMp, quickLinks, navigate }) {
  const showMpToggle = allowedMps.length > 1;

  const mpSwitcher = showMpToggle ? (
    <ToggleButtonGroup
      value={managerMp}
      exclusive
      size="small"
      onChange={(_e, v) => v && setManagerMp(v)}
    >
      {allowedMps.map((mp) => (
        <ToggleButton key={mp} value={mp} sx={{ textTransform: 'none' }}>
          {MP_SHORT[mp]}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  ) : (
    <Chip label={MP_NAMES[allowedMps[0]] || MP_SHORT[managerMp]} color="primary" variant="outlined" size="small" />
  );

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>{mpSwitcher}</Box>

      <BlockState
        loading={finance.loading}
        error={finance.error}
        empty={!finance.loading && !finance.error && kpi && !kpi.cur.hasData}
        minHeight={140}
      >
        {kpi && <FinanceKpiGrid kpi={kpi} extended />}
      </BlockState>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <DynamicsChart
            theme={theme}
            title={`Динамика за 14 дней · ${MP_NAMES[managerMp] || MP_SHORT[managerMp]}`}
            data={chartData}
            loading={finance.loading}
            error={finance.error}
          />
        </Grid>
      </Grid>

      <QuickLinksSection title="Что открыть дальше" links={quickLinks} navigate={navigate} />
    </>
  );
}

// ============================================================
// Пресет «Закупщик»
// ============================================================

function PurchaserView({ theme, canPurchasing, purchasing, purchasingChart, quickLinks, navigate }) {
  const data = purchasing.data;
  const pieceOf = (r) => Number(r.unitsCount ?? r.ordersCount ?? 0);
  const topProducts = (data?.byProduct || []).slice(0, 8).map((r) => ({ label: r.podgruppa, value: pieceOf(r) }));
  const topColors = (data?.byColor || []).slice(0, 10).map((r) => ({ label: r.article_number, value: pieceOf(r) }));
  const productsTotal = (data?.byProduct || []).reduce((s, r) => s + pieceOf(r), 0);
  const colorsTotal = (data?.byColor || []).reduce((s, r) => s + pieceOf(r), 0);
  const hasChart = purchasingChart.some((d) => d.unitsCount);

  return (
    <>
      {canPurchasing ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SectionCard title="Штуки за 14 дней" subtitle="Заказы × кратность карточки">
              <BlockState
                loading={purchasing.loading}
                error={purchasing.error}
                empty={!purchasing.loading && !purchasing.error && !hasChart}
                minHeight={280}
              >
                <Box sx={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={purchasingChart} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                      <XAxis dataKey="label" axisLine={false} tickLine={false} fontSize={12} />
                      <YAxis axisLine={false} tickLine={false} fontSize={12} allowDecimals={false} />
                      <Tooltip
                        cursor={{ fill: alpha(theme.palette.primary.main, 0.08) }}
                        contentStyle={{
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 8,
                        }}
                        formatter={(value) => [`${fmtInt(value)} шт`, 'Штуки']}
                      />
                      <Bar dataKey="unitsCount" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </BlockState>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard title="Топ подгрупп">
              <BlockState
                loading={purchasing.loading}
                error={purchasing.error}
                empty={!purchasing.loading && !purchasing.error && topProducts.length === 0}
                minHeight={200}
              >
                <TopList items={topProducts} total={productsTotal} color="primary" />
              </BlockState>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard title="Топ артикулов">
              <BlockState
                loading={purchasing.loading}
                error={purchasing.error}
                empty={!purchasing.loading && !purchasing.error && topColors.length === 0}
                minHeight={200}
              >
                <TopList items={topColors} total={colorsTotal} color="secondary" />
              </BlockState>
            </SectionCard>
          </Grid>
        </Grid>
      ) : (
        <Alert severity="info" variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
          Нет доступа к данным закупочного дашборда. Доступны быстрые переходы по вашим правам.
        </Alert>
      )}

      <QuickLinksSection title="Быстрые переходы" links={quickLinks} navigate={navigate} />
    </>
  );
}

// ============================================================
// Секция быстрых переходов
// ============================================================

function QuickLinksSection({ title, links, navigate }) {
  if (!links || links.length === 0) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="titleMedium" fontWeight={600} sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      <Grid container spacing={2}>
        {links.map((l, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={`${l.path}-${idx}`}>
            <QuickLink
              label={l.label}
              icon={l.icon}
              color={l.color}
              onClick={() => navigate(l.path)}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ============================================================
// Навигационный дашборд (нет прав на аналитические пресеты)
// ============================================================

function NavigationalView({ user, navigate }) {
  const menuItems = useMemo(() => getMenuItems(user), [user]);
  // Плоский список доступных разделов (кроме самого дашборда)
  const links = [];
  for (const item of menuItems) {
    if (item.path === '/') continue;
    if (item.children && item.children.length) {
      for (const child of item.children) {
        links.push({ label: child.text, path: child.path, section: item.text });
      }
    } else if (item.path) {
      links.push({ label: item.text, path: item.path, section: item.text });
    }
  }

  return (
    <>
      <Alert severity="info" variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        Аналитические сводки недоступны по вашим правам. Ниже — быстрый доступ к вашим рабочим разделам.
      </Alert>
      {links.length === 0 ? (
        <Typography color="text.secondary">Доступных разделов не найдено.</Typography>
      ) : (
        <Grid container spacing={2}>
          {links.map((l, idx) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={`${l.path}-${idx}`}>
              <QuickLink
                label={l.label}
                icon={<ArrowForwardIcon fontSize="small" />}
                color="primary"
                onClick={() => navigate(l.path)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
}
